
import { useState, useEffect, useCallback } from 'react';
import { ScheduledPopup } from '@/types/popup-system';
import { ActivePopup } from './types';

interface UseScheduledPopupsProps {
  setActivePopups: React.Dispatch<React.SetStateAction<ActivePopup[]>>;
}

export const useScheduledPopups = ({ setActivePopups }: UseScheduledPopupsProps) => {
  // Add error boundary for React hooks
  let popupQueue: ScheduledPopup[] = [];
  let setPopupQueue: React.Dispatch<React.SetStateAction<ScheduledPopup[]>> = () => {};
  
  try {
    [popupQueue, setPopupQueue] = useState<ScheduledPopup[]>([]);
  } catch (error) {
    console.warn('Hook error in useScheduledPopups, using fallback state:', error);
    // Fallback to empty state if hooks are corrupted
    popupQueue = [];
    setPopupQueue = () => {};
  }

  // Load persisted scheduled popups from localStorage on mount
  useEffect(() => {
    try {
      const savedQueue = localStorage.getItem('popup-queue');
      
      if (savedQueue) {
        const parsedQueue = JSON.parse(savedQueue).map((p: any) => ({
          ...p,
          scheduledFor: new Date(p.scheduledFor),
          createdAt: new Date(p.createdAt)
        }));
        setPopupQueue(parsedQueue);
      }
    } catch (error) {
      console.error('Error loading saved popups:', error);
    }
  }, []);

  // Save to localStorage whenever queue changes
  useEffect(() => {
    try {
      localStorage.setItem('popup-queue', JSON.stringify(popupQueue));
    } catch (error) {
      console.error('Error saving popup queue:', error);
    }
  }, [popupQueue]);

  // Check for scheduled popups
  const checkScheduledPopups = useCallback(() => {
    if (!Array.isArray(popupQueue)) return;
    
    const now = new Date();
    const duePopups = popupQueue.filter(
      popup => popup.status === 'pending' && popup.scheduledFor <= now
    );

    duePopups.forEach(scheduledPopup => {
      const popupContent: ActivePopup = {
        id: scheduledPopup.popupId,
        title: 'Scheduled Message',
        message: 'This is a scheduled popup message.',
        style: 'terminal',
        priority: 'medium',
        dismissible: true,
        scheduledId: scheduledPopup.id,
        triggerId: scheduledPopup.triggerId,
        persistForAllUsers: !scheduledPopup.targetUserId
      };

      try {
        setActivePopups(prev => [...prev, popupContent]);
        
        setPopupQueue(prev =>
          prev.map(p =>
            p.id === scheduledPopup.id
              ? { ...p, status: 'sent' as const }
              : p
          )
        );
      } catch (error) {
        console.error('Error updating popup state:', error);
      }
    });
  }, [popupQueue, setActivePopups]);

  useEffect(() => {
    const interval = setInterval(checkScheduledPopups, 30000);
    return () => clearInterval(interval);
  }, [checkScheduledPopups]);

  const addScheduledPopup = useCallback((popup: ScheduledPopup) => {
    try {
      setPopupQueue(prev => [...prev, popup]);
    } catch (error) {
      console.error('Error adding scheduled popup:', error);
    }
  }, []);

  return {
    popupQueue,
    addScheduledPopup
  };
};
